import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnInit } from "@angular/core";
import { FileUpload } from "@edp-models/file-upload.model";
import { RemoveUploadedFile } from "@edp-store/actions/file-upload.actions";
import { getUploadedFiles } from "@edp-store/selectors/file-upload.selectors";
import { getMimeType } from "@edp-utils/get-mimetype.utils";
import { whiteListFileTypes } from "@edp-utils/white-list-file-types.utils";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";

@Component({
    selector: "edp-drag-drop-files-upload",
    templateUrl: "./edp-drag-drop-files-upload.component.html",
    styleUrls: ["./edp-drag-drop-files-upload.component.scss"],
})
export class EdpDragDropFilesUploadComponent implements OnInit {
    @Input() fileData: FileUpload;
    @Input() filesMessageLabel: string;
    @Input() maxFileSizeMB: number;
    @Input() maxFileCount: number;
    @Input() allowedType: string;
    @Input() isFull: boolean;
    @Input() maxTotalSizeMB: number;
    @Input() fieldFileToToSet: string = null;
    @Input() valueFileToSet: string = null;
    @Output() uploaded = new EventEmitter();
    @Output() invalidUpload = new EventEmitter();
    @Output() scrolledList = new EventEmitter();

    @ViewChild("fileDropRef", { static: false }) fileDropRef: ElementRef;
    public uploadCompleted = false;
    public isOver: boolean;
    public uploadedFile$: Observable<FileUpload[]>;
    public isInvalid: boolean;
    public isInvalidType: boolean;
    public isInvalidTotalSize: boolean;
    public isInvalidExtension: boolean;
    public fileSize: string;
    public totalUploadedSize = 0;
    public files: FileUpload[] = [];
    public fileDropRefId = "fileDropRef";

    constructor(private readonly store: Store<{}>) {
        this.uploadedFile$ = this.store.pipe(select(getUploadedFiles));

        this.uploadedFile$.subscribe((files: FileUpload[]) => {
            this.files = !this.valueFileToSet ? files : files.filter(i => i[this.fieldFileToToSet] === this.valueFileToSet);
        });
    }

    ngOnInit() {
        if (this.valueFileToSet) {
            this.fileDropRefId = this.fileDropRefId + this.valueFileToSet;
        }
    }

    public removeFile(file) {
        this.fileDropRef.nativeElement.value = "";
        this.store.dispatch(new RemoveUploadedFile(file));

        if (this.maxTotalSizeMB) {
            this.totalUploadedSize -= file.rawSize;
            this.validateTotalSize();
        }
    }

    public formatBytes(bytes, decimals = 2) {
        if (bytes === 0) {
            return "0 Bytes";
        }

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    }

    private validateExtensionWhiteList(nameFile: string): boolean {
        return !!whiteListFileTypes.includes(getMimeType(nameFile));
    }

    private validateFile(selectedFile: any): boolean {
        if (!this.validateExtensionWhiteList(selectedFile.name)) {
            this.isInvalidExtension = true;
            return false;
        }

        if (selectedFile.size > Number(this.maxFileSizeMB * 1000000)) {
            this.isInvalid = true;
            return false;
        }

        if (this.allowedType && selectedFile.name.indexOf(this.allowedType) === -1) {
            this.isInvalidType = true;
            return false;
        }
        return true;
    }

    private resetValidateFlags(): void {
        this.isInvalidType = false;
        this.isInvalid = false;
        this.isInvalidExtension = false;
    }

    public fileDropped(file) {
        if (file.length) {
            this.isOver = false;
            const selectedFile = file[0];

            if (!this.validateFile(selectedFile)) {
                return;
            }

            this.resetValidateFlags();
            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onload = () => {
                this.fileData = {
                    name: selectedFile.name,
                    file: reader.result,
                    isLoading: true,
                    loaded: true,
                    size: this.fileSize,
                    rawSize: selectedFile.size,
                };
                if (this.valueFileToSet) {
                    this.fileData[this.fieldFileToToSet] = this.valueFileToSet;
                }
                this.uploaded.emit(this.fileData);

                if (this.maxTotalSizeMB) {
                    this.totalUploadedSize += selectedFile.size;
                    this.validateTotalSize();
                }
            };
        }
    }

    public validateTotalSize() {
        const totalExceeded = this.totalUploadedSize > Number(this.maxTotalSizeMB * 1000000);
        this.isInvalidTotalSize = totalExceeded;
        this.invalidUpload.emit(totalExceeded);
    }

    public onScrollFiles() {
        this.scrolledList.emit(true);
    }
}
